import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';
import type { Tone } from './badgeStyles';
import { cn } from './cn';
import { IconButton } from './IconButton';

const TONE_STYLES: Record<Tone, { box: string; icon: string }> = {
  neutral: { box: 'border-border bg-surface-sunken', icon: 'text-fg-muted' },
  accent: { box: 'border-accent/20 bg-accent-muted', icon: 'text-accent' },
  success: {
    box: 'border-success/25 bg-success-muted',
    icon: 'text-success-fg',
  },
  warning: {
    box: 'border-warning/30 bg-warning-muted',
    icon: 'text-warning-fg',
  },
  danger: { box: 'border-danger/25 bg-danger-muted', icon: 'text-danger-fg' },
};

const DEFAULT_ICONS: Record<Tone, ReactNode> = {
  neutral: <Info />,
  accent: <Info />,
  success: <CircleCheck />,
  warning: <TriangleAlert />,
  danger: <CircleAlert />,
};

export interface AlertProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'title'
> {
  tone?: Tone;
  title?: ReactNode;
  /** Replaces the tone's icon; `false` hides it. */
  icon?: ReactNode | false;
  /** Buttons for resolving the alert, e.g. `<Button size="sm">Re-solve</Button>`. */
  action?: ReactNode;
  /** Shows a close button that calls this handler. */
  onDismiss?: () => void;
  dismissLabel?: string;
}

/**
 * Inline message about the current screen. `danger` alerts use
 * `role="alert"` (announced immediately); other tones use `role="status"`.
 * Pass `role` to override.
 */
export const Alert = ({
  tone = 'neutral',
  title,
  icon,
  action,
  onDismiss,
  dismissLabel = 'Dismiss',
  className,
  children,
  role,
  ...props
}: AlertProps) => {
  const styles = TONE_STYLES[tone];
  const iconNode = icon === undefined ? DEFAULT_ICONS[tone] : icon;
  return (
    <div
      role={role ?? (tone === 'danger' ? 'alert' : 'status')}
      className={cn(
        'flex items-start gap-3 rounded-lg border px-3.5 py-3 text-sm',
        styles.box,
        className
      )}
      {...props}
    >
      {iconNode !== false && iconNode !== null && (
        <span
          aria-hidden="true"
          className={cn('mt-0.5 flex shrink-0 [&_svg]:size-4', styles.icon)}
        >
          {iconNode}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0 space-y-0.5">
          {title != null && <p className="font-medium text-fg">{title}</p>}
          {children != null && <div className="text-fg-muted">{children}</div>}
        </div>
        {action != null && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {action}
          </div>
        )}
      </div>
      {onDismiss && (
        <IconButton
          aria-label={dismissLabel}
          tooltip={false}
          icon={<X />}
          size="sm"
          onClick={onDismiss}
          className="-my-1.5 -mr-1.5 text-fg-muted hover:bg-fg/5"
        />
      )}
    </div>
  );
};
